/**
 * One game on the Pick'em slate (ISSUE-1304): both teams as full-width tap
 * targets, the line beside each team in a Beat the spread pool, the game's
 * own lock line, the live or final score, and once the pick locks its result
 * and points.
 *
 * Every fact is a server field on `/pickem/round-get`: `isPickable`,
 * `isLocked`, `locksAt`, `unavailableReason`, `spread`, `myPick.outcome` and
 * `myPick.points`. The picked side shows a solid brand border and a check,
 * never a tint alone.
 */
import { cn, FOCUS_RING } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { TeamMark } from "../gameDetail/TeamMark";
import type { typeTeamName } from "../pick/useTeamNames";
import {
  PICKEM_UNAVAILABLE_LABELS,
  PICKEM_YOUR_PICK_LABEL,
  pickemPointsLabel,
  pickemRankLabel,
} from "./pickemCopy";
import {
  PICKEM_TONE_CLASS,
  pickemLockLine,
  pickemOutcomeLabel,
  pickemSideSpreadLabel,
  pickemTeam,
} from "./pickemFormat";
import type { typeStagedPick } from "./pickemGameModel";
import type { typePickemGame } from "./pickemTypes";

const CheckIcon = (): ReactNode => (
  <svg
    className="h-icon-sm w-icon-sm shrink-0 text-primary"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="m3.5 8.5 3 3 6-7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export type typePickemGameCardProps = {
  game: typePickemGame;
  teamsById: ReadonlyMap<string, typeTeamName>;
  /** The staged pick on this game, saved or not. */
  stagedPick: typeStagedPick | null;
  isSpreadPool: boolean;
  /** viewer.canPick from the server. False shows the slate read only. */
  canPick: boolean;
  nowMs: number;
  timeZone: string;
  onTapTeam: (input: { game: typePickemGame; teamId: string }) => void;
};

function TeamButton(props: {
  game: typePickemGame;
  teamId: string;
  isHome: boolean;
  isSelected: boolean;
  isDisabled: boolean;
  isSpreadPool: boolean;
  score: number | null;
  teamsById: ReadonlyMap<string, typeTeamName>;
  onTap: () => void;
}): ReactNode {
  const team = pickemTeam(props.teamsById, props.teamId);
  const spreadLabel = props.isSpreadPool
    ? pickemSideSpreadLabel({
        homeLine: props.game.spread?.homeLine,
        isHome: props.isHome,
      })
    : null;
  return (
    <button
      type="button"
      aria-pressed={props.isSelected}
      disabled={props.isDisabled}
      data-testid={`pickem-team-${props.game.eventId}-${props.teamId}`}
      className={cn(
        "flex min-h-touch-target w-full items-center gap-3 rounded-md px-3 py-2 text-left",
        "transition-colors duration-200 motion-reduce:transition-none",
        props.isSelected
          ? "border-2 border-primary bg-surface"
          : "border border-line bg-surface",
        props.isDisabled
          ? "cursor-not-allowed"
          : "hover:bg-surface-sunken active:bg-surface-sunken",
        FOCUS_RING,
      )}
      onClick={props.onTap}
    >
      <TeamMark
        name={team.name}
        abbreviation={team.abbreviation}
        logoUrl={team.logoUrl}
        size="sm"
      />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-body-strong text-ink">{team.name}</span>
        {spreadLabel !== null ? (
          <span className="text-caption text-ink-secondary tabular-nums">
            {spreadLabel}
          </span>
        ) : null}
      </span>
      {props.isSelected ? (
        <span className="flex items-center gap-1 text-caption font-semibold text-primary">
          <CheckIcon />
          {PICKEM_YOUR_PICK_LABEL}
        </span>
      ) : null}
      {props.score !== null ? (
        <span className="text-body-strong text-ink tabular-nums">
          {props.score}
        </span>
      ) : null}
    </button>
  );
}

export const PickemGameCard = (props: typePickemGameCardProps): ReactNode => {
  const { game, stagedPick } = props;
  const isDisabled = !props.canPick || !game.isPickable || game.isLocked;
  const unavailable =
    game.unavailableReason === null || game.unavailableReason === undefined
      ? null
      : PICKEM_UNAVAILABLE_LABELS[game.unavailableReason];
  const lockLine =
    unavailable ??
    pickemLockLine({
      locksAt: game.locksAt,
      isLocked: game.isLocked,
      nowMs: props.nowMs,
      timeZone: props.timeZone,
    });
  const myPick = game.myPick ?? null;
  const showsResult = myPick !== null && myPick.isLocked;
  const rank = stagedPick?.confidenceRank ?? null;

  return (
    <article
      className="flex flex-col gap-2 rounded-md border border-hairline bg-surface p-3"
      data-testid={`pickem-game-${game.eventId}`}
      data-locked={game.isLocked ? "true" : "false"}
    >
      <div className="flex items-center justify-between gap-2">
        <span
          className="text-caption text-ink-secondary"
          data-testid={`pickem-game-lock-${game.eventId}`}
        >
          {lockLine}
        </span>
        {rank !== null ? (
          <span
            className="rounded-full border border-primary px-2 text-caption font-semibold text-ink"
            data-testid={`pickem-game-rank-${game.eventId}`}
          >
            {pickemRankLabel(rank)}
          </span>
        ) : null}
      </div>
      <TeamButton
        game={game}
        teamId={game.awayTeamId}
        isHome={false}
        isSelected={stagedPick?.teamId === game.awayTeamId}
        isDisabled={isDisabled}
        isSpreadPool={props.isSpreadPool}
        score={game.liveScore?.away ?? null}
        teamsById={props.teamsById}
        onTap={() => {
          props.onTapTeam({ game, teamId: game.awayTeamId });
        }}
      />
      <TeamButton
        game={game}
        teamId={game.homeTeamId}
        isHome
        isSelected={stagedPick?.teamId === game.homeTeamId}
        isDisabled={isDisabled}
        isSpreadPool={props.isSpreadPool}
        score={game.liveScore?.home ?? null}
        teamsById={props.teamsById}
        onTap={() => {
          props.onTapTeam({ game, teamId: game.homeTeamId });
        }}
      />
      {showsResult ? (
        <div
          className={cn(
            "flex items-center justify-between gap-2 rounded-md px-3 py-1 text-caption",
            PICKEM_TONE_CLASS[myPick.outcome],
          )}
          data-testid={`pickem-game-result-${game.eventId}`}
          data-tone={myPick.outcome}
        >
          <span className="font-semibold">
            {pickemOutcomeLabel(myPick.outcome)}
          </span>
          {typeof myPick.points === "number" ? (
            <span className="tabular-nums">
              {pickemPointsLabel(myPick.points)}
            </span>
          ) : null}
        </div>
      ) : null}
    </article>
  );
};
